import {
  IndexOptions,
  PartitionDeclaration,
  StateDeclaration,
  StateIndex,
  StateIndexKeys,
  StateIndexValue,
  StateIndices,
} from './state-declaration.js';
import { object } from '../typing/object.js';
import { ObjectTypeDescription } from '../typing/object-type-description.js';
import { TypeDescription } from '../typing/type-description.js';
import { getJsonSchemaDescription } from '../json-schema/json-schema.js';

export function declarePartition<
  Value extends {
    [key: string]: TypeDescription;
  },
  Key extends (keyof StateIndexValue<{
    [Prop in keyof Value & string]: Value[Prop]['_output'];
  }> &
    string)[],
>(
  type: Value,
  keys: Key,
): PartitionDeclaration<
  { [Prop in keyof Value & string]: Value[Prop]['_output'] },
  Key
> {
  const obj = object('partition', type);
  return {
    type: obj,
    schema: getJsonSchemaDescription(obj),
    key: keys,
  };
}

export function declareState<
  Name extends string,
  Value extends {
    [key: string]: TypeDescription;
  },
  Key extends keyof StateIndexValue<{
    [Prop in keyof Value & string]: Value[Prop]['_output'];
  }>,
>(
  name: Name,
  type: Value,
  keys: Key[],
): StateDeclaration<
  Name,
  { [Prop in keyof Value & string]: Value[Prop]['_output'] },
  Key,
  Record<string, never>
> {
  return stateIndexBuilder(name, object(name, type), keys, {});
}

export function declareStateFromObject<
  Name extends string,
  Obj extends ObjectTypeDescription<any>,
  Key extends keyof StateIndexValue<Obj['_output']> & string,
>(
  name: Name,
  type: Obj,
  key: Key[],
): StateDeclaration<Name, Obj['_output'], Key, {}> {
  return stateIndexBuilder(name, type, key, {});
}

function stateIndexBuilder<
  Name extends string,
  Value,
  Key extends keyof StateIndexValue<Value> & string,
  Index extends StateIndices<any>,
>(
  name: Name,
  type: ObjectTypeDescription<Value>,
  key: Key[],
  indices: Index,
): StateDeclaration<Name, Value, Key, Index> {
  return {
    name,
    type,
    schema: getJsonSchemaDescription(type),
    indices,
    key,
    index<K extends string, IK extends StateIndexKeys<Value>[]>(
      indexName: K,
      fields: IK,
      options: IndexOptions,
    ): StateDeclaration<
      Name,
      Value,
      Key,
      Index & Record<K, StateIndex<Value, IK>>
    > {
      return stateIndexBuilder<
        Name,
        Value,
        Key,
        Index & Record<K, StateIndex<Value, IK>>
      >(name, type, key, {
        ...indices,
        ...{
          [indexName]: {
            fields,
            unique: options?.unique ?? false,
          },
        },
      });
    },
  };
}
